import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ALL_ENABLED,
  DEFAULT_PREFERENCES,
  readConsent,
  saveConsent,
} from "@/lib/cookie-consent";
import { CookiePreferencesDialog } from "./CookiePreferencesDialog";

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const existing = readConsent();
    if (!existing) setVisible(true);

    const openHandler = () => setPrefsOpen(true);
    window.addEventListener("reserve:open-cookie-preferences", openHandler);
    return () =>
      window.removeEventListener(
        "reserve:open-cookie-preferences",
        openHandler,
      );
  }, []);

  const accept = (mode: "all" | "necessary") => {
    saveConsent(mode === "all" ? ALL_ENABLED : DEFAULT_PREFERENCES);
    setVisible(false);
  };

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie preferences"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-6 py-5 text-foreground shadow-2xl backdrop-blur"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="text-xs leading-relaxed text-muted-foreground md:max-w-2xl">
              We use strictly-necessary cookies to keep you signed in and secure
              the service. With your consent we also use functional, analytics,
              and marketing cookies. See our{" "}
              <Link to="/legal/cookies" className="underline hover:text-accent">
                Cookie Policy
              </Link>{" "}
              and{" "}
              <Link to="/legal/privacy" className="underline hover:text-accent">
                Privacy Policy
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setVisible(false);
                  setPrefsOpen(true);
                }}
                className="border border-border px-5 py-2 text-[10px] tracking-wide-luxury text-muted-foreground transition-colors hover:border-accent hover:text-accent"
              >
                Customise
              </button>
              <button
                type="button"
                onClick={() => accept("necessary")}
                className="border border-border px-5 py-2 text-[10px] tracking-wide-luxury text-muted-foreground transition-colors hover:border-accent hover:text-accent"
              >
                Necessary only
              </button>
              <button
                type="button"
                onClick={() => accept("all")}
                className="bg-accent px-5 py-2 text-[10px] tracking-wide-luxury text-primary-foreground transition-colors hover:bg-accent/90"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      <CookiePreferencesDialog
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
      />
    </>
  );
}
